import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { httpPost } from '../utils';
import { Button, ButtonStyles, Icon, Icons } from '@silverfort/sf-components';
import { useCloudResources } from '../../hooks/useCloudResources';

const CLOUD_RESOURCES_LOGIN_TIMEOUT = 30000; // 30 seconds

interface CloudResourcesLoginProps {
    onCloudLoginSuccess: () => void;
    onCloudLoginFailure: () => void;
    onCloudLoginCancel: () => void;
    hidden: boolean;
}

interface LoginData {
    code: string;
    baseUrl: string;
    orgId: string;
}

const LoginFailed = ({ onCancel, onConnectAnyway }) => (
    <div className="flex flex-col items-center gap-6">
        <div className="flax p-4 flex-col gap-2 border rounded-lg border-sf-neutral-500 bg-sf-neutral-100">
            <div className="flex gap-2 items-center">
                <Icon icon={Icons.CircleInfo} size={24} className="text-sf-neutral-500" />
                <span className="text-14 font-medium">Partial Connection</span>
            </div>
            <span className="text-13 font-normal">
                Cloud services are currently unavailable. You can still continue using Silverfort's platform
                capabilities by selecting Connect Anyway.
            </span>
        </div>
        <div className="flex gap-2 w-full">
            <Button
                className="[&&]:w-1/2 [&&]:justify-center"
                text="Cancel"
                onClick={onCancel}
                buttonStyle={ButtonStyles.GhostSolid}
            />
            <Button className="[&&]:w-1/2 [&&]:justify-center" onClick={onConnectAnyway} text="Connect Anyway" />
        </div>
    </div>
);

const LoginContainer: FC = ({ children }) => (
    <div className="bg-white items-center font-sf flex flex-col gap-8 w-[346px]">
        <div className="flex flex-col items-center">
            <span className="text-[18px] text-sf-gray-800 ">Welcome to Silverfort</span>
            <span className="text-12 text-sf-gray-500">The Identity Security Platform</span>
        </div>
        {children}
    </div>
);

const LoginLoader = () => (
    <div className="flex items-center bg-sf-gray-50 border border-sf-gray-100 p-4 gap-2 w-[282px] rounded-lg">
        <Icon icon={Icons.Loader} size={24} className="text-sf-gray-500 animate-spin" />
        <span className="text-14 text-sf-gray-800 font-medium">Connecting...</span>
    </div>
);

const LoginIFrame: FC<LoginData> = ({ code, orgId, baseUrl }) => {
    const origin = window.location.origin;

    return (
        <iframe
            className="hidden"
            src={`${baseUrl}/silverfort/v1/authorize?code=${code}&org=${orgId}&origin=${origin}`}
            title="cloud resources login iframe"
        />
    );
};

const CloudResourcesLogin: FC<CloudResourcesLoginProps> = ({
    onCloudLoginSuccess,
    onCloudLoginFailure,
    onCloudLoginCancel,
    hidden
}) => {
    const [loginData, setLoginData] = useState<LoginData | null>(null);
    const [isFail, setIsFail] = useState(false);
    const { updateCloudResourcesBaseUrl, updateIsCloudResourcesConnected } = useCloudResources();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const getOtp = async () => {
            try {
                const { data } = (await httpPost('/login/cloudResources', {})) as {
                    data: null | LoginData;
                };
                if (data) {
                    setLoginData(data);
                    updateCloudResourcesBaseUrl(data.baseUrl);
                    timeoutRef.current = setTimeout(() => {
                        onFailure();
                    }, CLOUD_RESOURCES_LOGIN_TIMEOUT);
                } else {
                    console.error('Failed to get OTP');
                    onFailure();
                }
            } catch (error) {
                console.error(error);
                onFailure();
            }
        };

        getOtp();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const onFailure = () => {
        setIsFail(true);
        updateIsCloudResourcesConnected(false);
        updateCloudResourcesBaseUrl('');
        onCloudLoginFailure();
    };

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin === loginData?.baseUrl) {
                if (event.data === 'LOGGED_IN') {
                    updateIsCloudResourcesConnected(true);
                    onCloudLoginSuccess();
                } else {
                    onFailure();
                }
            }
        };

        if (!loginData) return;

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [loginData]);

    const loginIframe = useMemo(() => {
        if (!loginData) return <></>;
        return <LoginIFrame {...loginData} />;
    }, [loginData]);

    return (
        <div className="z-[1]">
            {!hidden && (
                <LoginContainer>
                    {isFail ? (
                        <LoginFailed onCancel={onCloudLoginCancel} onConnectAnyway={onCloudLoginSuccess} />
                    ) : (
                        <LoginLoader />
                    )}
                </LoginContainer>
            )}
            {loginIframe}
        </div>
    );
};

export default CloudResourcesLogin;
